import { useEffect, useMemo, useState, useCallback } from "react";
import {
  ArrowUpRight,
  Ban,
  CheckCircle2,
  Loader2,
  MailPlus,
  RefreshCw,
  Send,
  ShieldAlert,
  ShieldCheck,
  Undo2,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import HUDFrame from "@/components/HUDFrame";

const PERSONAS = [
  { value: "dual_income", label: "Dual-Income Family" },
  { value: "chronic_illness", label: "Chronic Illness Household" },
  { value: "single_parent", label: "Single Parent" },
  { value: "sandwich_generation", label: "Sandwich Generation" },
  { value: "military_veteran", label: "Military / Veteran Family" },
  { value: "underserved", label: "Underserved Family" },
  { value: "other", label: "Other" },
];
const personaLabel = Object.fromEntries(PERSONAS.map((p) => [p.value, p.label]));

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

// ---------- deliverability visuals ----------
const STATUS_META = {
  queued: { label: "Queued", tone: "mist", ring: "border-mist/40 text-mist" },
  sent: { label: "Sent", tone: "teal", ring: "border-teal/50 text-teal" },
  delivery_delayed: { label: "Delayed", tone: "amber", ring: "border-signalAmber/50 text-signalAmber" },
  delivered: { label: "Delivered", tone: "green", ring: "border-signalGreen/50 text-signalGreen" },
  opened: { label: "Opened", tone: "green", ring: "border-signalGreen/60 text-signalGreen" },
  clicked: { label: "Clicked", tone: "green", ring: "border-signalGreen/70 text-signalGreen" },
  bounced: { label: "Bounced", tone: "red", ring: "border-signalRed/60 text-signalRed" },
  complained: { label: "Complained", tone: "red", ring: "border-signalRed/60 text-signalRed" },
  failed: { label: "Failed", tone: "red", ring: "border-signalRed/60 text-signalRed" },
};

const DELIVERY_FILTER_OPTIONS = [
  ["any", "Any status"],
  ["delivered", "Delivered+"],
  ["opened", "Opened+"],
  ["bounced", "Bounced"],
  ["suppressed", "Suppressed"],
];

function StatusPill({ status, suppressed }) {
  if (suppressed) {
    return (
      <span className="inline-flex items-center gap-1 border border-signalRed/60 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-signalRed">
        <Ban className="h-3 w-3" strokeWidth={1.5} />
        Suppressed
      </span>
    );
  }
  const meta = STATUS_META[status] || null;
  if (!meta) {
    return (
      <span className="inline-block border border-rule px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
        —
      </span>
    );
  }
  return (
    <span className={`inline-block border ${meta.ring} px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em]`}>
      {meta.label}
    </span>
  );
}

// ---------- timeline dialog ----------
function TimelineDialog({ entry, onClose, onUnsuppress }) {
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entry) return;
    let cancelled = false;
    api
      .get(`/admin/waitlist/${entry.id}/events`)
      .then((r) => !cancelled && setEvents(r.data))
      .catch(() => !cancelled && setEvents([]));
    return () => {
      cancelled = true;
    };
  }, [entry]);

  const doUnsuppress = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/waitlist/${entry.id}/unsuppress`);
      toast.success(`Unsuppressed ${entry.email}. You can re-invite now.`);
      onUnsuppress?.(entry.id);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unsuppress.");
    } finally {
      setBusy(false);
    }
  };

  if (!entry) return null;

  return (
    <div
      data-testid="timeline-dialog"
      className="fixed inset-0 z-50 flex items-end justify-center bg-obsidian/70 backdrop-blur-sm md:items-center"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl border border-rule bg-deep p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="timeline-close"
          className="absolute right-4 top-4 border border-rule p-2 text-mist hover:border-amber hover:text-ink"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>

        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
          Deliverability · timeline
        </div>
        <h2 className="mt-3 font-display text-2xl text-ink" data-testid="timeline-email">
          {entry.email}
        </h2>
        <div className="mt-1 flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
          <span>{personaLabel[entry.persona] || entry.persona}</span>
          <span>·</span>
          <span>{entry.household_size}</span>
          <span>·</span>
          <span>Joined {fmt(entry.created_at)}</span>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <StatusPill status={entry.email_status} suppressed={entry.suppressed} />
          {entry.suppressed && (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-signalRed">
              reason · {entry.suppressed_reason}
            </span>
          )}
        </div>

        {entry.suppressed && (
          <button
            onClick={doUnsuppress}
            disabled={busy}
            data-testid="timeline-unsuppress-btn"
            className="mt-6 inline-flex items-center gap-2 border border-signalRed/50 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-signalRed transition-colors duration-300 hover:border-signalRed hover:bg-signalRed/10 disabled:opacity-40"
          >
            <Undo2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            {busy ? "Working…" : "Override suppression"}
          </button>
        )}

        <div className="mt-8 border-t border-rule pt-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            Event history
          </div>
          <div className="mt-4">
            {events === null ? (
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-mist">
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                Loading events…
              </div>
            ) : events.length === 0 ? (
              <p className="text-sm text-mist">
                No email events yet. Send an invite to start the timeline.
              </p>
            ) : (
              <ol className="relative space-y-4 border-l border-rule pl-6" data-testid="timeline-events">
                {events.map((ev) => {
                  const meta = STATUS_META[ev.event_type] || {
                    label: ev.event_type,
                    ring: "border-mist/40 text-mist",
                  };
                  return (
                    <li key={ev.id} className="relative">
                      <span
                        className={`absolute -left-[27px] top-1 h-2.5 w-2.5 border ${meta.ring}`}
                        style={{ background: "#0C1525" }}
                      />
                      <div className="flex items-baseline gap-3">
                        <span className={`font-mono text-[11px] uppercase tracking-[0.22em] ${meta.ring.split(" ").slice(-1)[0]}`}>
                          {meta.label}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                          {fmtTime(ev.created_at)}
                        </span>
                      </div>
                      {ev.subject && (
                        <div className="mt-1 text-xs text-mist">{ev.subject}</div>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- cohort telemetry card ----------
function CohortCard({ cohort, onRefresh }) {
  if (!cohort) {
    return (
      <HUDFrame className="bg-deep/40 p-6" tone="mist">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
          Last cohort
        </div>
        <p className="mt-4 text-sm text-mist">
          No cohorts dispatched yet. Send a bulk invite to see delivery telemetry here.
        </p>
      </HUDFrame>
    );
  }

  const rows = [
    ["Sent", cohort.sent, "text-teal"],
    ["Delivered", cohort.delivered, "text-signalGreen"],
    ["Opened", cohort.opened, "text-signalGreen"],
    ["Bounced", cohort.bounced, "text-signalRed"],
    ["Complained", cohort.complained, "text-signalRed"],
  ];
  const pct = (v) => (cohort.total ? Math.round((v / cohort.total) * 100) : 0);

  return (
    <HUDFrame className="bg-deep/60 p-6 md:p-8" tone="teal">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
            Last cohort · telemetry
          </div>
          <div className="mt-2 font-display text-2xl text-ink" data-testid="cohort-title">
            {cohort.persona ? personaLabel[cohort.persona] || cohort.persona : "Mixed cohort"}
            <span className="ml-2 font-mono text-sm text-mist">· {cohort.total}</span>
          </div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
            Dispatched {fmtTime(cohort.created_at)} · cohort {cohort.cohort_id.slice(-6)}
          </div>
        </div>
        <button
          onClick={onRefresh}
          data-testid="cohort-refresh"
          className="border border-rule p-2.5 text-mist hover:border-teal hover:text-ink"
          aria-label="Refresh telemetry"
        >
          <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-5" data-testid="cohort-stats">
        {rows.map(([label, count, color]) => (
          <div key={label}>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
              {label}
            </div>
            <div className={`mt-2 font-mono text-2xl ${color}`}>{count}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
              {pct(count)}%
            </div>
          </div>
        ))}
      </div>
    </HUDFrame>
  );
}

// ---------- main page ----------
export default function AdminWaitlist() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [entries, setEntries] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [err, setErr] = useState(null);
  const [pending, setPending] = useState(null);
  const [invitedFilter, setInvitedFilter] = useState("all");
  const [personaFilter, setPersonaFilter] = useState("all");
  const [deliveryFilter, setDeliveryFilter] = useState("any");
  const [bulkLimit, setBulkLimit] = useState(10);
  const [bulkSending, setBulkSending] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadWaitlist = useCallback(async () => {
    const wl = await api.get("/admin/waitlist");
    setEntries(wl.data);
  }, []);
  const loadCohort = useCallback(async () => {
    try {
      const r = await api.get("/admin/cohorts/latest");
      setCohort(r.data || null);
    } catch {
      setCohort(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/status")
      .then(async (r) => {
        if (cancelled) return;
        setStatus(r.data);
        if (r.data.is_admin) {
          await loadWaitlist();
          await loadCohort();
        }
      })
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Admin unavailable."));
    return () => {
      cancelled = true;
    };
  }, [loadWaitlist, loadCohort]);

  const invite = async (id, email) => {
    setPending(id);
    try {
      const { data } = await api.post(`/admin/waitlist/${id}/invite`);
      if (data.already_invited) toast.info(`${email} was already invited.`);
      else toast.success(`Invite sent to ${email}.`);
      await loadWaitlist();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not send invite.";
      toast.error(typeof msg === "string" ? msg : "Could not send invite.");
    } finally {
      setPending(null);
    }
  };

  const bulkInvite = async () => {
    setBulkSending(true);
    try {
      const body = { limit: Number(bulkLimit) || 10 };
      if (personaFilter !== "all") body.persona = personaFilter;
      const { data } = await api.post("/admin/waitlist/bulk-invite", body);
      if (data.invited_count === 0) {
        toast.info("No eligible households to invite with those filters.");
      } else {
        toast.success(
          `Cohort dispatched — ${data.invited_count} invite${data.invited_count === 1 ? "" : "s"} sent.`
        );
      }
      if (data.suppressed_skipped > 0) {
        toast.info(`Skipped ${data.suppressed_skipped} suppressed address${data.suppressed_skipped === 1 ? "" : "es"}.`);
      }
      if (data.failed_ids?.length) {
        toast.error(`${data.failed_ids.length} send${data.failed_ids.length === 1 ? "" : "s"} failed. Check backend logs.`);
      }
      await Promise.all([loadWaitlist(), loadCohort()]);
      setConfirmBulk(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk invite failed.");
    } finally {
      setBulkSending(false);
    }
  };

  const unsuppressed = (id) => {
    setEntries((prev) =>
      prev ? prev.map((e) => (e.id === id ? { ...e, suppressed: false, suppressed_reason: null } : e)) : prev
    );
  };

  // ---------- derived ----------
  const filtered = useMemo(() => {
    if (!entries) return [];
    return entries.filter((e) => {
      if (invitedFilter === "invited" && !e.invited) return false;
      if (invitedFilter === "not-invited" && e.invited) return false;
      if (personaFilter !== "all" && e.persona !== personaFilter) return false;
      if (deliveryFilter === "suppressed" && !e.suppressed) return false;
      if (deliveryFilter === "delivered") {
        if (!["delivered", "opened", "clicked"].includes(e.email_status)) return false;
      }
      if (deliveryFilter === "opened") {
        if (!["opened", "clicked"].includes(e.email_status)) return false;
      }
      if (deliveryFilter === "bounced") {
        if (!["bounced", "complained", "failed"].includes(e.email_status)) return false;
      }
      return true;
    });
  }, [entries, invitedFilter, personaFilter, deliveryFilter]);

  const personaCounts = useMemo(() => {
    if (!entries) return {};
    return entries.reduce((acc, e) => {
      const p = e.persona || "other";
      if (!acc[p]) acc[p] = { total: 0, waiting: 0 };
      acc[p].total += 1;
      if (!e.invited && !e.suppressed) acc[p].waiting += 1;
      return acc;
    }, {});
  }, [entries]);

  const eligibleCount = useMemo(() => {
    if (!entries) return 0;
    return entries.filter((e) => {
      if (e.invited || e.suppressed) return false;
      if (personaFilter !== "all" && e.persona !== personaFilter) return false;
      return true;
    }).length;
  }, [entries, personaFilter]);

  const toDispatch = Math.min(eligibleCount, Number(bulkLimit) || 0);

  // ---------- render guards ----------
  if (err) return <div data-testid="admin-error" className="mx-auto max-w-3xl px-6 py-16 text-mist">{String(err)}</div>;
  if (!status)
    return (
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-16 font-mono text-[11px] uppercase tracking-[0.22em] text-mist">
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        Verifying clearance…
      </div>
    );

  if (!status.is_admin) {
    return (
      <div data-testid="admin-forbidden" className="mx-auto max-w-2xl px-6 py-24">
        <div className="border border-rule bg-deep/40 p-8">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-signalAmber">
            <ShieldAlert className="h-4 w-4" strokeWidth={1.5} />
            Restricted
          </div>
          <h1 className="mt-4 font-display text-3xl text-ink">Admin only.</h1>
          <p className="mt-4 text-sm leading-relaxed text-mist">
            Your account (<span className="font-mono text-ink">{status.email}</span>) is not on the admin allowlist.
          </p>
        </div>
      </div>
    );
  }

  const total = entries?.length ?? 0;
  const invitedTotal = entries?.filter((e) => e.invited).length ?? 0;
  const waitingTotal = entries?.filter((e) => !e.invited && !e.suppressed).length ?? 0;
  const suppressedTotal = entries?.filter((e) => e.suppressed).length ?? 0;

  return (
    <div data-testid="admin-waitlist-page" className="mx-auto max-w-7xl px-6 py-10 md:px-8 md:py-12">
      <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
        <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
        A-00 · Admin · Cohort router
      </div>
      <h1 className="mt-4 font-display text-4xl leading-[1.05] text-ink md:text-5xl">The manifest.</h1>
      <p className="mt-4 max-w-xl text-base leading-relaxed text-mist">
        Every household that requested clearance. Track deliverability, filter by profile, and
        dispatch cohorts with one call. Signed in as <span className="font-mono text-ink">{user?.email}</span>.
      </p>

      {/* stats */}
      <div className="mt-8 grid gap-4 md:grid-cols-4">
        <HUDFrame className="bg-deep/40 p-5" tone="teal">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <Users className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            Total
          </div>
          <div className="mt-2 font-mono text-3xl text-signalGreen" data-testid="admin-stat-total">
            {total}
          </div>
        </HUDFrame>
        <HUDFrame className="bg-deep/40 p-5" tone="teal">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">Invited</div>
          <div className="mt-2 font-mono text-3xl text-signalAmber" data-testid="admin-stat-invited">
            {invitedTotal}
          </div>
        </HUDFrame>
        <HUDFrame className="bg-deep/40 p-5" tone="teal">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">Awaiting</div>
          <div className="mt-2 font-mono text-3xl text-teal" data-testid="admin-stat-waiting">
            {waitingTotal}
          </div>
        </HUDFrame>
        <HUDFrame className="bg-deep/40 p-5" tone={suppressedTotal ? "amber" : "teal"}>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">Suppressed</div>
          <div
            className={`mt-2 font-mono text-3xl ${suppressedTotal ? "text-signalRed" : "text-mist"}`}
            data-testid="admin-stat-suppressed"
          >
            {suppressedTotal}
          </div>
        </HUDFrame>
      </div>

      {/* cohort telemetry */}
      <div className="mt-8">
        <CohortCard cohort={cohort} onRefresh={loadCohort} />
      </div>

      {/* persona chips */}
      <div className="mt-8 border-t border-rule pt-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">By profile</div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-persona-chips">
          <button
            onClick={() => setPersonaFilter("all")}
            data-testid="admin-persona-chip-all"
            className={`flex items-baseline gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-200 ${
              personaFilter === "all"
                ? "border-teal bg-deep text-ink"
                : "border-rule bg-obsidian text-mist hover:border-teal/50"
            }`}
          >
            All
            <span className="text-teal">{total}</span>
          </button>
          {PERSONAS.map((p) => {
            const stats = personaCounts[p.value] || { total: 0, waiting: 0 };
            if (stats.total === 0) return null;
            const active = personaFilter === p.value;
            return (
              <button
                key={p.value}
                onClick={() => setPersonaFilter(p.value)}
                data-testid={`admin-persona-chip-${p.value}`}
                className={`flex items-baseline gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-200 ${
                  active ? "border-teal bg-deep text-ink" : "border-rule bg-obsidian text-mist hover:border-teal/50"
                }`}
              >
                {p.label}
                <span className="text-teal">{stats.total}</span>
                {stats.waiting > 0 && (
                  <span className="border-l border-rule/60 pl-2 text-signalAmber">
                    {stats.waiting} wait
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* cohort dispatcher */}
      <HUDFrame className="mt-8 bg-deep/60 p-6 md:p-8" tone="amber">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="max-w-lg">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-amber">
              <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
              Cohort dispatcher
            </div>
            <p className="mt-3 font-display text-xl leading-snug text-ink md:text-2xl">
              Invite <span className="text-signalGreen">{toDispatch}</span> household
              {toDispatch === 1 ? "" : "s"}{" "}
              {personaFilter === "all" ? (
                <span className="text-mist">across all profiles</span>
              ) : (
                <span className="text-mist">
                  from <span className="text-ink">{personaLabel[personaFilter]}</span>
                </span>
              )}
              .
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
              {eligibleCount} awaiting · suppressed excluded · oldest-first
            </p>
          </div>

          <div className="flex items-end gap-3">
            <div>
              <label
                htmlFor="bulk-limit"
                className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist"
              >
                Batch size
              </label>
              <input
                id="bulk-limit"
                type="number"
                min="1"
                max="200"
                value={bulkLimit}
                onChange={(e) => setBulkLimit(e.target.value)}
                data-testid="admin-bulk-limit"
                className="mt-2 w-24 border border-rule bg-obsidian px-3 py-2.5 font-mono text-sm text-ink focus:border-teal focus:outline-none"
              />
            </div>
            {confirmBulk ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={bulkInvite}
                  disabled={bulkSending || toDispatch === 0}
                  data-testid="admin-bulk-confirm-btn"
                  className="flex items-center gap-2 bg-amber px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-obsidian transition-colors duration-300 hover:bg-amber/80 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {bulkSending ? "Dispatching…" : `Confirm · ${toDispatch}`}
                </button>
                <button
                  onClick={() => setConfirmBulk(false)}
                  disabled={bulkSending}
                  data-testid="admin-bulk-cancel-btn"
                  className="border border-rule px-4 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-mist hover:border-amber hover:text-ink"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmBulk(true)}
                disabled={toDispatch === 0}
                data-testid="admin-bulk-invite-btn"
                className="flex items-center gap-2 bg-teal px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal-hover disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send className="h-3.5 w-3.5" strokeWidth={1.5} />
                Dispatch cohort
              </button>
            )}
          </div>
        </div>
      </HUDFrame>

      {/* filters */}
      <div className="mt-8 flex flex-wrap items-center gap-2">
        {[
          ["all", "All"],
          ["not-invited", "Awaiting"],
          ["invited", "Invited"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setInvitedFilter(k)}
            data-testid={`admin-filter-${k}`}
            className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-200 ${
              invitedFilter === k
                ? "border-teal bg-deep text-ink"
                : "border-rule bg-obsidian text-mist hover:border-teal/50"
            }`}
          >
            {label}
          </button>
        ))}

        <div className="mx-2 h-6 w-px bg-rule" />

        <label
          htmlFor="delivery-filter"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist"
        >
          Deliverability:
        </label>
        <select
          id="delivery-filter"
          value={deliveryFilter}
          onChange={(e) => setDeliveryFilter(e.target.value)}
          data-testid="admin-delivery-filter"
          className="appearance-none border border-rule bg-obsidian px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink focus:border-teal focus:outline-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1L6 6L11 1' stroke='%239BA6B5' stroke-width='1.5'/></svg>\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 0.5rem center",
            paddingRight: "1.75rem",
          }}
        >
          {DELIVERY_FILTER_OPTIONS.map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>

        {personaFilter !== "all" && (
          <button
            onClick={() => setPersonaFilter("all")}
            data-testid="admin-persona-clear"
            className="border border-rule px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-signalAmber hover:border-amber"
          >
            Clear persona · {personaLabel[personaFilter]} ×
          </button>
        )}
      </div>

      {/* manifest table */}
      <HUDFrame className="mt-6 bg-deep/40 p-6" tone="teal">
        {!entries ? (
          <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-mist">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            Loading manifest…
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-mist">No entries match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table
              className="w-full min-w-[860px] border-collapse text-left"
              data-testid="admin-manifest-table"
            >
              <thead>
                <tr className="border-b border-rule text-mist">
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">Email</th>
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">Size</th>
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">Profile</th>
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">Joined</th>
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">Invited</th>
                  <th className="py-3 font-mono text-[10px] uppercase tracking-[0.22em]">
                    Delivery
                  </th>
                  <th className="py-3 text-right font-mono text-[10px] uppercase tracking-[0.22em]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={`admin-row-${e.id}`}
                    className={`border-b border-rule/60 last:border-b-0 hover:bg-obsidian/40 ${e.suppressed ? "opacity-70" : ""}`}
                  >
                    <td className="py-3">
                      <button
                        onClick={() => setDetail(e)}
                        data-testid={`admin-row-email-${e.id}`}
                        className="font-mono text-sm text-ink underline decoration-rule underline-offset-4 hover:decoration-teal"
                        title="View delivery timeline"
                      >
                        {e.email}
                      </button>
                    </td>
                    <td className="py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-mist">
                      {e.household_size}
                    </td>
                    <td className="py-3 text-sm text-mist">
                      {personaLabel[e.persona] || e.persona}
                    </td>
                    <td className="py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-mist">
                      {fmt(e.created_at)}
                    </td>
                    <td className="py-3">
                      {e.invited ? (
                        <span className="inline-flex items-center gap-1 border border-signalGreen/50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-signalGreen">
                          <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
                          Sent
                        </span>
                      ) : (
                        <span className="inline-block border border-rule px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
                          Waiting
                        </span>
                      )}
                    </td>
                    <td className="py-3" data-testid={`admin-row-status-${e.id}`}>
                      <StatusPill status={e.email_status} suppressed={e.suppressed} />
                    </td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() => invite(e.id, e.email)}
                        disabled={e.invited || e.suppressed || pending === e.id}
                        data-testid={`admin-invite-btn-${e.id}`}
                        className="inline-flex items-center gap-2 border border-rule px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink transition-colors duration-200 hover:border-teal disabled:cursor-not-allowed disabled:opacity-40"
                        title={
                          e.suppressed
                            ? "Suppressed — open timeline to unsuppress"
                            : e.invited
                              ? "Already invited"
                              : "Send invite"
                        }
                      >
                        <MailPlus className="h-3 w-3" strokeWidth={1.5} />
                        {pending === e.id
                          ? "Sending…"
                          : e.suppressed
                            ? "Blocked"
                            : e.invited
                              ? "Sent"
                              : "Send invite"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </HUDFrame>

      <TimelineDialog
        entry={detail}
        onClose={() => setDetail(null)}
        onUnsuppress={(id) => {
          unsuppressed(id);
          loadWaitlist();
        }}
      />
    </div>
  );
}
